import { useEffect, useState } from 'react';
import { ApiError, get } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { useSession } from '../session.js';
import { fmtDateTime } from '../util.js';

interface Run {
  id: string;
  batchId: string;
  source: string;
  by: string;
  state: 'running' | 'done' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  log: string[];
  validation: string | null;
  report: { inserted?: Record<string, number>; updated?: Record<string, number>; unchanged?: Record<string, number>; quarantined?: number } | null;
  reconciliation: string[] | null;
  error: string | null;
}
interface Batch {
  id: string;
  source: string;
  stage: string;
  status: string | null;
  startedAt: string;
  finishedAt: string | null;
  counts: Record<string, number> | null;
}

/** Διαχείριση → Εισαγωγή HPress: the batches this instance has received and what became of them. */
export function AdminImportPage() {
  const { can } = useSession();
  const [runs, setRuns] = useState<Run[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [open, setOpen] = useState<Run | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = () =>
      get<{ runs: Run[]; batches: Batch[] }>('/api/admin/import/batches')
        .then((r) => {
          if (cancelled) return;
          setRuns(r.runs);
          setBatches(r.batches);
          setError(null);
        })
        .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : String(e)));
    tick();
    const h = window.setInterval(tick, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(h);
    };
  }, []);

  if (!can('admin.config')) return <div className="banner err">{t('noPermission')}</div>;
  const sum = (m?: Record<string, number>) => Object.values(m ?? {}).reduce((a, b) => a + b, 0);

  return (
    <>
      <PageTitle title={t('importTitle')} hint={t('importHint')} />
      {error && <div className="banner err">{error}</div>}
      <h3 className="panel-title">{t('importRuns')}</h3>
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('importStarted')}</th>
              <th>{t('importBatch')}</th>
              <th>{t('importBy')}</th>
              <th>{t('status')}</th>
              <th>{t('importInserted')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr>
                <td colSpan={6} className="c muted">
                  {t('importNone')}
                </td>
              </tr>
            )}
            {runs.map((r) => (
              <tr key={r.id}>
                <td className="c small">{fmtDateTime(r.startedAt)}</td>
                <td className="small">
                  <code>{r.batchId.slice(0, 8)}</code> · {r.source}
                </td>
                <td>{r.by}</td>
                <td className="c">
                  <span className={`status ${r.state === 'done' ? 'ok' : r.state === 'failed' ? 'err' : 'warn'}`}>{r.state}</span>
                  {r.error && <div className="small">{r.error}</div>}
                </td>
                <td className="num">{r.report ? sum(r.report.inserted) : '—'}</td>
                <td className="actions">
                  <button title={t('importLog')} onClick={() => setOpen(r)}>
                    📄
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">
              {t('importLog')} — {open.batchId.slice(0, 8)}
            </span>
            <button className="btn" onClick={() => setOpen(null)}>
              {t('close')}
            </button>
          </div>
          <pre className="import-log">{open.log.join('\n')}</pre>
          {open.report && (
            <pre className="import-log">
              {t('importInserted')}: {JSON.stringify(open.report.inserted)}
              {'\n'}
              {t('importUnchanged')}: {JSON.stringify(open.report.unchanged)}
              {'\n'}quarantined: {open.report.quarantined ?? 0}
            </pre>
          )}
          {open.reconciliation && <pre className="import-log">{open.reconciliation.join('\n')}</pre>}
          {open.validation && <pre className="import-log">{open.validation}</pre>}
        </div>
      )}
      <h3 className="panel-title">{t('importBatches')}</h3>
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('importBatch')}</th>
              <th>{t('importSource')}</th>
              <th>{t('importStarted')}</th>
              <th>{t('status')}</th>
              <th>{t('importCounts')}</th>
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 && (
              <tr>
                <td colSpan={5} className="c muted">
                  —
                </td>
              </tr>
            )}
            {batches.map((b) => (
              <tr key={b.id}>
                <td>
                  <code>{b.id.slice(0, 8)}</code>
                </td>
                <td>{b.source}</td>
                <td className="c small">{fmtDateTime(b.startedAt)}</td>
                <td className="c">{b.status ?? b.stage}</td>
                <td className="small">{b.counts ? JSON.stringify(b.counts) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tip">{t('importTip')}</p>
    </>
  );
}
